import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs,
  setDoc 
} from 'firebase/firestore';
import { db } from '../firebase';
import './AdminPanel.css';

function AdminPanel({ onLogout }) {
  const [activeTab, setActiveTab] = useState('gallery');
  const [galleryItems, setGalleryItems] = useState([]);
  const [musicItems, setMusicItems] = useState([]);
  const [aboutText, setAboutText] = useState({ paragraph1: '', paragraph2: '' });
  
  // Form states
  const [newGalleryItem, setNewGalleryItem] = useState({ url: '', title: '' });
  const [newMusicItem, setNewMusicItem] = useState({ title: '', subtitle: '', year: '', link: '' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load gallery items
      const gallerySnapshot = await getDocs(collection(db, 'gallery'));
      const gallery = gallerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGalleryItems(gallery);

      // Load music items
      const musicSnapshot = await getDocs(collection(db, 'music'));
      const music = musicSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMusicItems(music);

      // Load about text
      const aboutSnapshot = await getDocs(collection(db, 'about'));
      if (!aboutSnapshot.empty) {
        const aboutData = aboutSnapshot.docs[0].data();
        setAboutText(aboutData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Gallery CRUD operations
  const addGalleryItem = async () => {
    if (!newGalleryItem.url || !newGalleryItem.title) {
      alert('Please fill in all fields');
      return;
    }
    try {
      await addDoc(collection(db, 'gallery'), {
        ...newGalleryItem,
        order: galleryItems.length
      });
      setNewGalleryItem({ url: '', title: '' });
      loadData();
    } catch (error) {
      console.error('Error adding gallery item:', error);
    }
  };

  const updateGalleryItem = async (id, data) => {
    try {
      await updateDoc(doc(db, 'gallery', id), data);
      loadData();
      setEditingId(null);
    } catch (error) {
      console.error('Error updating gallery item:', error);
    }
  };

  const deleteGalleryItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, 'gallery', id));
        loadData();
      } catch (error) {
        console.error('Error deleting gallery item:', error);
      }
    }
  };

  // Music CRUD operations
  const addMusicItem = async () => {
    if (!newMusicItem.title || !newMusicItem.subtitle || !newMusicItem.year) {
      alert('Please fill in all required fields (title, subtitle, year)');
      return;
    }
    try {
      await addDoc(collection(db, 'music'), {
        ...newMusicItem,
        order: musicItems.length
      });
      setNewMusicItem({ title: '', subtitle: '', year: '', link: '' });
      loadData();
    } catch (error) {
      console.error('Error adding music item:', error);
    }
  };

  const updateMusicItem = async (id, data) => {
    try {
      await updateDoc(doc(db, 'music', id), data);
      loadData();
      setEditingId(null);
    } catch (error) {
      console.error('Error updating music item:', error);
    }
  };

  const deleteMusicItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, 'music', id));
        loadData();
      } catch (error) {
        console.error('Error deleting music item:', error);
      }
    }
  };

  // About section update
  const updateAbout = async () => {
    try {
      const aboutSnapshot = await getDocs(collection(db, 'about'));
      if (aboutSnapshot.empty) {
        await setDoc(doc(db, 'about', 'content'), aboutText);
      } else {
        await updateDoc(doc(db, 'about', aboutSnapshot.docs[0].id), aboutText);
      }
      alert('About section updated successfully!');
    } catch (error) {
      console.error('Error updating about:', error);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </div>

      <div className="admin-tabs">
        <button 
          className={activeTab === 'gallery' ? 'active' : ''} 
          onClick={() => setActiveTab('gallery')}
        >
          Gallery
        </button>
        <button 
          className={activeTab === 'music' ? 'active' : ''} 
          onClick={() => setActiveTab('music')}
        >
          Music
        </button>
        <button 
          className={activeTab === 'about' ? 'active' : ''} 
          onClick={() => setActiveTab('about')}
        >
          About
        </button>
      </div>

      <div className="admin-content">
        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tab-content"
          >
            <h2>Gallery Management</h2>
            
            <div className="add-form">
              <h3>Add New Image</h3>
              <input
                type="text"
                placeholder="Image URL"
                value={newGalleryItem.url}
                onChange={(e) => setNewGalleryItem({ ...newGalleryItem, url: e.target.value })}
              />
              <input
                type="text"
                placeholder="Image Title"
                value={newGalleryItem.title}
                onChange={(e) => setNewGalleryItem({ ...newGalleryItem, title: e.target.value })}
              />
              <button onClick={addGalleryItem} className="add-btn">Add Image</button>
            </div>

            <div className="items-list">
              <h3>Current Images</h3>
              {galleryItems.map((item) => (
                <div key={item.id} className="item-card">
                  {editingId === item.id ? (
                    <div className="edit-form">
                      <input
                        type="text"
                        defaultValue={item.url}
                        onBlur={(e) => updateGalleryItem(item.id, { url: e.target.value })}
                      />
                      <input
                        type="text"
                        defaultValue={item.title}
                        onBlur={(e) => updateGalleryItem(item.id, { title: e.target.value })}
                      />
                      <button onClick={() => setEditingId(null)}>Done</button>
                    </div>
                  ) : (
                    <>
                      <img src={item.url} alt={item.title} className="item-preview" />
                      <div className="item-info">
                        <h4>{item.title}</h4>
                        <p className="item-url">{item.url}</p>
                      </div>
                      <div className="item-actions">
                        <button onClick={() => setEditingId(item.id)} className="edit-btn">Edit</button>
                        <button onClick={() => deleteGalleryItem(item.id)} className="delete-btn">Delete</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Music Tab */}
        {activeTab === 'music' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tab-content"
          >
            <h2>Music Management</h2>
            
            <div className="add-form">
              <h3>Add New Music Item</h3>
              <input
                type="text"
                placeholder="Title (e.g., Latest Album)"
                value={newMusicItem.title}
                onChange={(e) => setNewMusicItem({ ...newMusicItem, title: e.target.value })}
              />
              <input
                type="text"
                placeholder="Subtitle (e.g., Echoes of Tomorrow)"
                value={newMusicItem.subtitle}
                onChange={(e) => setNewMusicItem({ ...newMusicItem, subtitle: e.target.value })}
              />
              <input
                type="text"
                placeholder="Year"
                value={newMusicItem.year}
                onChange={(e) => setNewMusicItem({ ...newMusicItem, year: e.target.value })}
              />
              <input
                type="url"
                placeholder="Link (e.g., https://spotify.com/... or https://music.apple.com/...)"
                value={newMusicItem.link}
                onChange={(e) => setNewMusicItem({ ...newMusicItem, link: e.target.value })}
              />
              <button onClick={addMusicItem} className="add-btn">Add Music Item</button>
            </div>

            <div className="items-list">
              <h3>Current Music Items</h3>
              {musicItems.map((item) => (
                <div key={item.id} className="item-card">
                  {editingId === item.id ? (
                    <div className="edit-form">
                      <input
                        type="text"
                        defaultValue={item.title}
                        onBlur={(e) => updateMusicItem(item.id, { title: e.target.value })}
                      />
                      <input
                        type="text"
                        defaultValue={item.subtitle}
                        onBlur={(e) => updateMusicItem(item.id, { subtitle: e.target.value })}
                      />
                      <input
                        type="text"
                        defaultValue={item.year}
                        onBlur={(e) => updateMusicItem(item.id, { year: e.target.value })}
                      />
                      <input
                        type="url"
                        defaultValue={item.link || ''}
                        placeholder="Link (optional)"
                        onBlur={(e) => updateMusicItem(item.id, { link: e.target.value })}
                      />
                      <button onClick={() => setEditingId(null)}>Done</button>
                    </div>
                  ) : (
                    <>
                      <div className="item-info">
                        <h4>{item.title}</h4>
                        <p>{item.subtitle}</p>
                        <span className="year-badge">{item.year}</span>
                        {item.link && (
                          <a 
                            href={item.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="music-link-badge"
                          >
                            🔗 Link
                          </a>
                        )}
                      </div>
                      <div className="item-actions">
                        <button onClick={() => setEditingId(item.id)} className="edit-btn">Edit</button>
                        <button onClick={() => deleteMusicItem(item.id)} className="delete-btn">Delete</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tab-content"
          >
            <h2>About Section</h2>
            
            <div className="about-form">
              <h3>Edit About Text</h3>
              <textarea
                placeholder="First Paragraph"
                value={aboutText.paragraph1}
                onChange={(e) => setAboutText({ ...aboutText, paragraph1: e.target.value })}
                rows="5"
              />
              <textarea
                placeholder="Second Paragraph"
                value={aboutText.paragraph2}
                onChange={(e) => setAboutText({ ...aboutText, paragraph2: e.target.value })}
                rows="5"
              />
              <button onClick={updateAbout} className="save-btn">Save Changes</button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;

